
import { useState, useEffect } from "react";
import { useSession } from "@/hooks/useSession";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ResourceFile } from "./types";
import { isQuizFile, isFreeResponseFile, isDragDropFile, isLinkingFile } from "./utils/fileTypeUtils";

export function useTrainingCompletion(resourceId: string, files: ResourceFile[], isTrainee: boolean, isAdmin: boolean) {
  const [completedFiles, setCompletedFiles] = useState<{[key: string]: boolean}>({});
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const { session } = useSession();
  const { toast } = useToast();
  
  // Calculate overall progress
  const overallProgress = files.length > 0 && isTrainee
    ? Math.round(
        (Object.values(completedFiles).filter(Boolean).length / files.length) * 100
      )
    : 0;
    
  // This function checks if a file is locked (not accessible yet)
  const isFileLocked = (index: number) => {
    if (isAdmin || !isTrainee) return false;
    
    if (index === 0) return false;
    
    const isCurrentFileInteractive = files[index] && (
      isQuizFile(files[index]) || 
      isFreeResponseFile(files[index]) || 
      isDragDropFile(files[index]) || 
      isLinkingFile(files[index])
    );
    
    if (isCurrentFileInteractive) {
      const hasCompletedNonInteractive = files.some((file, i) => {
        return i < index && 
               !isQuizFile(file) && 
               !isFreeResponseFile(file) && 
               !isDragDropFile(file) && 
               !isLinkingFile(file) && 
               completedFiles[file.id] === true;
      });
      
      return !hasCompletedNonInteractive;
    }
    
    const prevFile = files[index - 1];
    if (prevFile) {
      if (
        isQuizFile(prevFile) || 
        isFreeResponseFile(prevFile) || 
        isDragDropFile(prevFile) || 
        isLinkingFile(prevFile)
      ) {
        return false;
      }
      
      return !completedFiles[prevFile.id];
    }
    
    return false;
  };
  
  // This function marks a file as completed
  const markFileCompleted = async (fileId: string) => {
    if (!session?.user?.id || !isTrainee || isAdmin) return;
    
    try {
      const { count, error: countError } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('id', session.user.id);
        
      if (countError) {
        console.error('Error checking user profile:', countError);
      }
      
      if ((count === null || count === 0) && !countError) {
        console.log('Creating profile for user:', session.user.id);
        const { data: userData } = await supabase.auth.getUser();
        const userMeta = userData?.user?.user_metadata || {};
        
        await supabase
          .from('profiles')
          .insert({
            id: session.user.id,
            email: session.user.email,
            company: userMeta.company || '',
            role: userMeta.role || 'trainee',
            subscription_tier: 'free',
            resources_used_this_month: 0,
            total_resources_created: 0
          });
      }
      
      await supabase
        .from('training_progress')
        .upsert({
          resource_id: resourceId,
          file_id: fileId,
          user_id: session.user.id,
          completed: true,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,file_id',
          ignoreDuplicates: false
        });
      
      setCompletedFiles(prev => ({
        ...prev,
        [fileId]: true
      }));
      
      const updatedCompletedFiles = {
        ...completedFiles,
        [fileId]: true
      };
      
      const completedCount = Object.values(updatedCompletedFiles).filter(Boolean).length;
      const totalItems = files.length;
      
      const calculatedProgress = totalItems > 0 
        ? Math.round((completedCount / totalItems) * 100)
        : 0;
      
      console.log('Updated progress calculation:', {
        fileId,
        completedCount,
        totalItems,
        calculatedProgress
      });
      
      const allFilesCompleted = files.every(file => updatedCompletedFiles[file.id] === true);
      
      let completionTimestamp = null;
      
      if (allFilesCompleted && !completedAt) {
        completionTimestamp = new Date().toISOString();
        setCompletedAt(completionTimestamp);
      }
      
      const { data: existingShareData, error: shareError } = await supabase
        .from('resource_shares')
        .select('completed_at')
        .eq('resource_id', resourceId)
        .eq('trainee_id', session.user.id)
        .maybeSingle();
      
      if (shareError) {
        console.error('Error fetching existing share data:', shareError);
      }
      
      try {
        await supabase
          .from('resource_shares')
          .upsert({ 
            resource_id: resourceId,
            trainee_id: session.user.id,
            status: 'accepted',
            progress: calculatedProgress,
            completed_at: allFilesCompleted ? (completionTimestamp || completedAt || existingShareData?.completed_at) : null
          }, {
            onConflict: 'resource_id,trainee_id',
            ignoreDuplicates: false
          });
      } catch (upsertError) {
        console.error('Error updating resource share:', upsertError);
      }
    } catch (error) {
      console.error('Error marking file completed:', error);
    }
  };
  
  // Fetch the completion status for all files
  const fetchCompletionStatus = async (resourceFiles: ResourceFile[]) => {
    if (!session?.user?.id || resourceFiles.length === 0 || isAdmin) return;
    
    try {
      console.log("Fetching completion status for files:", resourceFiles.map(f => f.id));
      
      const { data, error } = await supabase
        .from('training_progress')
        .select('*')
        .eq('user_id', session.user.id)
        .in('file_id', resourceFiles.map(f => f.id));

      if (error) throw error;

      const newCompletedStatus: {[key: string]: boolean} = {};
      
      resourceFiles.forEach(file => {
        newCompletedStatus[file.id] = false;
      });
      
      if (data) {
        console.log("File progress data:", data);
        
        data.forEach(progress => {
          if (progress.completed) {
            newCompletedStatus[progress.file_id] = true;
          }
        });
      }
      
      const quizFiles = resourceFiles.filter(file => isQuizFile(file));
      if (quizFiles.length > 0) {
        for (const quizFile of quizFiles) {
          if (quizFile.quiz_id) {
            const { data: quizAttempts, error: quizError } = await supabase
              .from('quiz_attempts')
              .select('*')
              .eq('quiz_id', quizFile.quiz_id)
              .eq('trainee_id', session.user.id)
              .eq('passed', true)
              .limit(1);
              
            if (quizError) {
              console.error('Error fetching quiz attempts:', quizError);
            } else if (quizAttempts && quizAttempts.length > 0) {
              console.log('Found completed quiz attempt:', quizAttempts[0]);
              newCompletedStatus[quizFile.id] = true;
            }
          }
        }
      }
      
      const dragDropFiles = resourceFiles.filter(file => isDragDropFile(file));
      if (dragDropFiles.length > 0) {
        for (const dragDropFile of dragDropFiles) {
          const fileIdToCheck = dragDropFile.file_id || dragDropFile.id;
          try {
            const { data: dragDropAttempts, error: dragDropError } = await supabase
              .from('drag_drop_attempts')
              .select('*')
              .eq('file_id', fileIdToCheck)
              .eq('trainee_id', session.user.id)
              .eq('passed', true)
              .limit(1);
              
            if (dragDropError) {
              console.error('Error fetching drag drop attempts:', dragDropError);
              continue;
            }
                
            if (dragDropAttempts && dragDropAttempts.length > 0) {
              newCompletedStatus[dragDropFile.id] = true;
            }
          } catch (err) {
            console.error('Error processing drag drop file:', err);
          }
        }
      }
      
      console.log('Setting completed files:', newCompletedStatus);
      setCompletedFiles(newCompletedStatus);
      
      const completedCount = Object.values(newCompletedStatus).filter(Boolean).length;
      const totalItems = resourceFiles.length;
      
      const calculatedProgress = totalItems > 0 
        ? Math.round((completedCount / totalItems) * 100)
        : 0;
      
      console.log('Progress calculation:', {
        completedCount,
        totalItems,
        calculatedProgress,
        completedFiles: newCompletedStatus,
        allCompleted: calculatedProgress === 100
      });
      
      if (calculatedProgress === 100) {
        const { data: existingShareData, error: shareError } = await supabase
          .from('resource_shares')
          .select('completed_at')
          .eq('resource_id', resourceId)
          .eq('trainee_id', session.user.id)
          .maybeSingle();
          
        if (shareError) {
          console.error('Error fetching share data:', shareError);
        } else if (existingShareData?.completed_at) {
          setCompletedAt(existingShareData.completed_at);
        }
      }
      
    } catch (error) {
      console.error('Error fetching completion status:', error);
      toast({
        title: "Error loading progress",
        description: "Your progress may not display correctly",
        variant: "destructive",
      });
    }
  };
  
  // Initialize the completion status when the component mounts
  useEffect(() => {
    if (isTrainee && session?.user?.id) {
      fetchCompletionStatus(files);
    }
  }, [isTrainee, session?.user?.id, files]);
  
  return {
    completedFiles,
    completedAt,
    isFileLocked,
    overallProgress,
    markFileCompleted,
    setCompletedFiles
  };
}
